import { useState, type ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * A bejelentkezes elotti kepernyok kozos kerete.
 *
 * Belepes, kotelezo jelszovaltas, jelszo-visszaallitas - mind ugyanaz a
 * kartya ugyanazon a hattermezon. Egy felhasznalo nem tudja, hogy kozben
 * masik utvonalon jar; a folytonossag megnyugtato.
 */
import { LogoTile } from './Logo';
import { readAppIcon } from '../lib/app-icon';

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="relative grid h-full place-items-center overflow-hidden p-6">
      {/* Halk, meleg hattermezo - melyseg ellaposodas nelkul. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(60rem 40rem at 15% -10%, var(--accent-soft), transparent 60%), radial-gradient(50rem 35rem at 90% 110%, var(--alert-soft), transparent 55%)',
        }}
      />

      <div className="animate-fade-rise relative w-full max-w-sm rounded-card border border-border-soft bg-surface-raised p-8 shadow-(--shadow-modal)">
        <div className="mb-6 text-center">
          {/* A valasztott paletta mar a belepo kepernyon is - ugyanaz a csempe
              fogadja az embert, mint ami a talcan van. */}
          <LogoTile icon={readAppIcon()} className="mx-auto mb-4 size-12 rounded-[0.9rem]" />
          <h1 className="font-display text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-ink-muted">{subtitle}</p>}
        </div>

        {children}

        <p className="mt-6 text-center text-2xs text-ink-faint">
          Saját, self-hosted rendszer — az adatok nem hagyják el a céget.
        </p>
      </div>
    </div>
  );
}

/**
 * Jelszomezo megmutatas-kapcsoloval.
 *
 * A lathatova tetel nem kenyelmi funkcio: egy 12+ karakteres jelszot vagy
 * egy levelbol kimasolt ideiglenes kodot enelkul sokan elgepelnek, es a
 * "hibas jelszo" uzenet utan mar nem tudjak, mit rontottak el.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete = 'new-password',
  hint,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  hint?: string;
  autoFocus?: boolean;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-muted">{label}</span>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          required
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-control border border-border bg-surface-raised py-2.5 pl-3 pr-11 text-sm"
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Jelszó elrejtése' : 'Jelszó megmutatása'}
          className="absolute inset-y-0 right-0 grid w-10 place-items-center text-ink-faint transition-colors hover:text-ink"
        >
          <Icon name={visible ? 'rejt' : 'mutat'} />
        </button>
      </div>
      {hint && <span className="mt-1 block text-2xs text-ink-faint">{hint}</span>}
    </label>
  );
}
